import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { apiFetch } from "./api";
import { formatNumber } from "./utils/format";
import Papa from "papaparse";



const todayStr = () => new Date().toISOString().slice(0, 10);

export default function Stock() {
  
  const navigate = useNavigate();
  const role = localStorage.getItem("role");
  const isAdmin = role === "admin";
  const [msg, setMsg] = useState("");
  const [loading, setLoading] = useState(false);
  const [products, setProducts] = useState([]);  const [searchProducts, setSearchProducts] = useState([]);
  const [query, setQuery] = useState("");
  const [sortBy, setSortBy] = useState("name");
  const [sortDir, setSortDir] = useState("asc");
  const [editingBarcode, setEditingBarcode] = useState(null);
  const [editValues, setEditValues] = useState({ name: "", invoicePrice: "", billingPrice: "", stock: "" });
  const [showAddStock, setShowAddStock] = useState(false);
  const [showReportsMenu, setShowReportsMenu] = useState(false);
  const [showStockMenu, setShowStockMenu] = useState(false);
  const reportsMenuRef = React.useRef(null);
  const stockMenuRef = React.useRef(null);
  const [barcode, setBarcode] = useState("");
  const [name, setName] = useState("");
  const [invoicePrice, setInvoicePrice] = useState("");
  const [billingPrice, setBillingPrice] = useState("");
  const [stock, setStock] = useState("");
  const [supplierName, setSupplierName] = useState("");
  const [supplierInvoiceNo, setSupplierInvoiceNo] = useState("");
  const [supplierPaymentMethod, setSupplierPaymentMethod] = useState("cash");
  const [receivedDate, setReceivedDate] = useState(todayStr());
  const [invoicePhoto, setInvoicePhoto] = useState("");


  const stockImportInputRef = React.useRef(null);
  const handleImportStock = async (event) => {
  const file = event.target.files?.[0];
  event.target.value = "";
  if (!file) return;

  const toNum = (v, d = 0) => {
    const n = Number(String(v ?? "").replace(/,/g, "").trim());
    return Number.isFinite(n) ? n : d;
  };

  try {
    setLoading(true);
    setMsg("");

    const parsed = await new Promise((resolve, reject) => {
  Papa.parse(file, {
    header: true,
    skipEmptyLines: true,
    transformHeader: (h) => String(h || "").trim().toLowerCase().replace(/\s+/g, ""),
    complete: (result) => resolve(result.data || []),
    error: reject,
  });
});


    let created = 0, updated = 0, skipped = 0, failed = 0;

    for (const row of parsed) {
      const barcode = String(row.barcode || row.code || "").trim();
      const name = String(row.name || row.productname || "").trim();
      if (!barcode) { skipped += 1; continue; }

      try {
        let existing = null;
        try { existing = await apiFetch(`/products/${encodeURIComponent(barcode)}`); } catch {}

        const invoicePrice = toNum(row.invoicePrice ?? row.invoiceprice, Number(existing?.invoicePrice || 0));
        const billingPrice = toNum(row.billingPrice ?? row.billingprice ?? row.price, Number(existing?.price || 0));
        const stockValue = toNum(row.stock, Number(existing?.stock || 0));
        const safeName = name || String(existing?.name || "").trim();
        if (!safeName) { skipped += 1; continue; }

        if (existing) {
          await apiFetch(`/products/${encodeURIComponent(barcode)}`, {
            method: "PUT",
            body: JSON.stringify({ name: safeName, invoicePrice, billingPrice, stock: stockValue }),
          });
          updated += 1;
        } else {
          await apiFetch("/products", {
            method: "POST",
            body: JSON.stringify({ barcode, name: safeName, invoicePrice, billingPrice, stock: stockValue }),
          });
          created += 1;
        }
      } catch {
        failed += 1;
      }
    }

    await loadProducts();
    setMsg(`Import complete: ${created} created, ${updated} updated, ${skipped} skipped, ${failed} failed`);
  } catch (e) {
    setMsg("Error: " + e.message);
  } finally {
    setLoading(false);
  }
};


  const doLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("role");
    navigate("/login");
  };

  const loadProducts = useCallback(async () => {
    setMsg("");
    setLoading(true);
    try {
      const data = await apiFetch("/products");
      setProducts(Array.isArray(data) ? data : []);
    } catch (e) {
      setMsg(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadProducts();
  }, [loadProducts]);

  useEffect(() => {
    const q = query.trim();
    if (!q) {
      setSearchProducts([]);
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const data = await apiFetch("/products");
        if (!cancelled) {
          setSearchProducts(Array.isArray(data) ? data : []);
        }
      } catch {
        if (!cancelled) setSearchProducts([]);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [query]);

  useEffect(() => {
    const onDocClick = (event) => {
      if (reportsMenuRef.current && !reportsMenuRef.current.contains(event.target)) {
        setShowReportsMenu(false);
      }
      if (stockMenuRef.current && !stockMenuRef.current.contains(event.target)) {
        setShowStockMenu(false);
      }
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  const visibleProducts = useMemo(() => {
    const normalize = (v) => String(v ?? "").toLowerCase();
    const q = normalize(query);
    const source = q ? searchProducts : products;
    const filtered = source.filter((p) => {
      if (!q) return true;
      return normalize(p.name).includes(q) || normalize(p.barcode).includes(q);
    });
    return [...filtered].sort((a, b) => {
      const dir = sortDir === "asc" ? 1 : -1;
      if (sortBy === "name") return normalize(a.name).localeCompare(normalize(b.name)) * dir;
      const aNum = Number(String(a[sortBy] ?? "").replace(/,/g, "")) || 0;
      const bNum = Number(String(b[sortBy] ?? "").replace(/,/g, "")) || 0;
      return (aNum - bNum) * dir;
    });
  }, [products, searchProducts, query, sortBy, sortDir]);

  const startEdit = (product) => {
    setEditingBarcode(product.barcode);
    setEditValues({
      name: product.name,
      invoicePrice: product.invoicePrice ?? "",
      billingPrice: product.price ?? "",
      stock: product.stock,
    });
  };

  const cancelEdit = () => {
    setEditingBarcode(null);
    setEditValues({ name: "", invoicePrice: "", billingPrice: "", stock: "" });
  };

  const saveEdit = async (barcodeValue) => {
    try {
      await apiFetch(`/products/${barcodeValue}`, {
        method: "PUT",
        body: JSON.stringify({
          name: editValues.name,
          invoicePrice: editValues.invoicePrice,
          billingPrice: editValues.billingPrice,
          stock: Number(editValues.stock),
        }),
      });
      setMsg("Product updated");
      cancelEdit();
      await loadProducts();
    } catch (e) {
      setMsg("Error: " + e.message);
    }
  };

  const deleteProduct = async (barcodeValue) => {
    if (!window.confirm("Delete this product?")) return;
    try {
      await apiFetch(`/products/${barcodeValue}`, { method: "DELETE" });
      setMsg("Product deleted");
      await loadProducts();
    } catch (e) {
      setMsg("Error: " + e.message);
    }
  };

  const createProduct = async (e) => {
    e.preventDefault();
    setMsg("");
    try {
      await apiFetch("/products", {
        method: "POST",
        body: JSON.stringify({
          barcode,
          name,
          invoicePrice: Number(invoicePrice || 0),
          billingPrice: Number(billingPrice || 0),
          stock: Number(stock || 0),
          supplierName: supplierName || null,
          supplierInvoiceNo: supplierInvoiceNo || null,
          supplierPaymentMethod: supplierPaymentMethod || null,
          receivedDate: receivedDate || null,
          invoicePhoto: invoicePhoto || null,
        }),
      });
      setMsg("Product created");
      setBarcode("");
      setName("");
      setInvoicePrice("");
      setBillingPrice("");
      setStock("");
      setSupplierName("");
      setSupplierInvoiceNo("");
      setSupplierPaymentMethod("cash");
      setReceivedDate(todayStr());
      setInvoicePhoto("");
      setShowAddStock(false);
      await loadProducts();
    } catch (e) {
      setMsg("Error: " + e.message);
    }
  };

  const downloadStockCsv = async () => {
    try {
      setLoading(true);
      const data = await apiFetch("/products");
      const rows = Array.isArray(data) ? data : [];
      const csvRows = [
        ["Barcode", "Name", "Invoice Price", "Billing Price", "Stock", "Total Value", "Supplier"],
        ...rows.map((p) => [
          p.barcode ?? "",
          p.name ?? "",
          Number(p.invoicePrice || 0),
          Number(p.price || 0),
          Number(p.stock || 0),
          Number(p.price || 0) * Number(p.stock || 0),
          p.supplierName || "",
        ]),
      ];
      const csv = csvRows.map((r) => r.map((v) => `"${String(v).replace(/"/g, "\"\"")}"`).join(",")).join("\n");
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "current_stock.csv";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      setMsg("Error: " + e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="admin-shell">
      <style>{`
        @import url("https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@400;500;600;700&display=swap");
        .admin-shell {
          font-family: "Space Grotesk", sans-serif;
          color: var(--text);
          background:
            radial-gradient(1200px 500px at 10% -10%, rgba(34, 193, 181, 0.15) 0%, rgba(34, 193, 181, 0) 60%),
            radial-gradient(900px 400px at 90% -20%, rgba(245, 158, 11, 0.12) 0%, rgba(245, 158, 11, 0) 55%),
            var(--bg);
          min-height: 100vh;
          padding: 24px 20px 40px;
        }
        .admin-content { max-width: 1200px; margin: 0 auto; }
        .topbar { display: flex; align-items: center; justify-content: space-between; gap: 16px; flex-wrap: wrap; margin-bottom: 18px; }
        .title h2 { margin: 0; font-size: 26px; }
        .title span { font-size: 13px; color: var(--muted); }
        .actions { display: flex; gap: 10px; flex-wrap: wrap; }
        .menu-wrap { position: relative; }
        .menu-panel {
          position: absolute;
          top: calc(100% + 6px);
          right: 0;
          display: grid;
          gap: 6px;
          padding: 8px;
          background: var(--panel);
          border: 1px solid var(--border);
          border-radius: 10px;
          z-index: 20;
          min-width: 170px;
        }
        .btn { border: none; border-radius: 10px; padding: 9px 14px; font-weight: 600; cursor: pointer; background: var(--accent); color: var(--bg); }
        .btn.secondary { background: var(--panel); color: var(--text); border: 1px solid var(--border); }
        .btn.danger { background: #dc2626; color: #fff; border: 1px solid #b91c1c; }
        .btn.add-stock { background: #86efac; color: #052e16; border: 1px solid #4ade80; }
        .btn.ghost { background: transparent; border: 1px solid var(--border); color: var(--text); }
        .banner { background: var(--panel); color: var(--text); border: 1px solid var(--border); padding: 10px 14px; border-radius: 10px; font-weight: 600; margin-bottom: 16px; }
        .panel { background: var(--panel); border: 1px solid var(--border); border-radius: 12px; padding: 14px; }
        .table-tools { display: flex; justify-content: space-between; align-items: center; gap: 10px; flex-wrap: wrap; margin-bottom: 12px; }
        .table-tools input, .table-tools select, .form input, .form select { padding: 8px 10px; border-radius: 8px; border: 1px solid var(--border); background: #fff; color: #000; }
        .form input, .form select { width: 100%; box-sizing: border-box; }
        .form { display: grid; gap: 10px; }
        .form-grid-2 { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); row-gap: 10px; column-gap: 0; }
        .form-full { grid-column: 1 / -1; }
        .input-group label { font-size: 12px; font-weight: 600; color: var(--muted); }
        .payment-row { display: flex; gap: 10px; align-items: center; flex-wrap: wrap; }
        .modal-actions { display: flex; gap: 10px; justify-content: flex-end; margin-top: 4px; }
        table { width: 100%; border-collapse: collapse; font-size: 14px; }
        thead th { text-align: left; padding: 10px; background: rgba(2, 2, 2, 0.99); border-bottom: 1px solid var(--border); }
        tbody td { padding: 10px; border-bottom: 1px solid var(--border); }
        tbody tr:hover { background: #ffffff; color: #000000; }
        tbody tr:hover td { color: #000000; }
        .stock-table-wrap { max-height: 440px; overflow-y: auto; overflow-x: auto; border: 1px solid var(--border); border-radius: 10px; }
        .stock-table-wrap table { width: 100%; border-collapse: collapse; }
        .stock-table-wrap thead th { position: sticky; top: 0; z-index: 2; }
        .stock-table-wrap tbody td { height: 44px; box-sizing: border-box; }
        .overlay { position: fixed; inset: 0; background: rgba(0,0,0,0.45); display: flex; align-items: center; justify-content: center; padding: 16px; z-index: 9999; }
        .modal { background: var(--panel); color: var(--text); border: 1px solid var(--border); border-radius: 12px; padding: 16px; width: min(640px, 100%); max-height: 88vh; overflow: auto; }
        @media (max-width: 720px) {
          .form-grid-2 { grid-template-columns: 1fr; }
          .modal-actions { justify-content: stretch; }
          .modal-actions .btn { flex: 1; }
        }
      `}</style>

      <div className="admin-content">
        <div className="topbar">
          <div className="title">
            <h2>Stock</h2>
           

            <span>Search, edit, and delete items</span>
          </div>
          <div className="actions">
            <button className="btn ghost" onClick={() => navigate(isAdmin ? "/admin" : "/cashier")}>Home</button>
            <div className="menu-wrap" ref={reportsMenuRef}>
              <button className="btn ghost" onClick={() => setShowReportsMenu((v) => !v)}>Reports</button>
              {showReportsMenu && (
                <div className="menu-panel">
                  <button className="btn secondary" onClick={() => { setShowReportsMenu(false); navigate("/reports"); }}>
                    Sales Reports
                  </button>
                  <button className="btn secondary" onClick={() => { setShowReportsMenu(false); navigate("/reports/items"); }}>
                    Item-wise Report
                  </button>
                </div>
              )}
            </div>
            <button className="btn ghost" onClick={() => navigate("/returns")}>Returns</button>
            <div className="menu-wrap" ref={stockMenuRef}>
              <button className="btn ghost" onClick={() => setShowStockMenu((v) => !v)}>Stock</button>
              {showStockMenu && (
                <div className="menu-panel">
                  <button className="btn secondary" onClick={() => { setShowStockMenu(false); navigate("/stock"); }}>
                    Current Stock
                  </button>
                  <button className="btn secondary" onClick={() => { setShowStockMenu(false); navigate("/stock/returned"); }}>
                    Returned Stock
                  </button>
                </div>
              )}
            </div>
            {isAdmin && <button className="btn ghost" onClick={() => navigate("/customers")}>Customers</button>}
            {isAdmin && <button className="btn secondary" onClick={() => navigate("/end-day")}>End Day</button>}
            {isAdmin && <button className="btn ghost" onClick={() => navigate("/billing")}>Billing</button>}
            <button className="btn danger" onClick={doLogout}>Logout</button>
            {isAdmin && <button className="btn add-stock" onClick={() => setShowAddStock(true)}>Add Stock</button>}
            
            <button className="btn secondary" onClick={() => window.print()}>Print</button>
          </div>
        </div>

        {msg && <div className="banner">{msg}</div>}

        <div className="panel">
          <div className="table-tools">
            <div>
              <h3 style={{ margin: 0 }}>Current Stock</h3>
              <span style={{ color: "var(--muted)", fontSize: 12 }}>Auto-loaded on page open</span>
            </div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              <input placeholder="Search by name or barcode" value={query} onChange={(e) => setQuery(e.target.value)} />
              <select value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
                <option value="name">Sort by name</option>
                <option value="price">Sort by price</option>
                <option value="stock">Sort by stock</option>
              </select>
              <select value={sortDir} onChange={(e) => setSortDir(e.target.value)}>
                <option value="asc">Ascending</option>
                <option value="desc">Descending</option>
              </select>
              <button className="btn secondary" type="button" onClick={loadProducts} disabled={loading}>
                Refresh
              </button>
               {isAdmin && (
  <>
    <button className="btn secondary" type="button" onClick={() => stockImportInputRef.current?.click()} disabled={loading}>
      Import Stock
    </button>
    <input
      ref={stockImportInputRef}
      type="file"
      accept=".csv,text/csv"
      style={{ display: "none" }}
      onChange={handleImportStock}
    />
  </>
)}

              <button className="btn secondary" type="button" onClick={downloadStockCsv} disabled={loading}>
                Export Stock
              </button>
            </div>
          </div>

          <div className="stock-table-wrap">
          <table>
            <thead>
              <tr>
                <th>Barcode</th>
                <th>Name</th>
                <th>Invoice Price</th>
                <th>Billing Price</th>
                <th>Stock</th>
                <th>Total Value</th>
                <th>Supplier</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {visibleProducts.map((p) => {
                const isEditing = editingBarcode === p.barcode;
                return (
                  <tr key={p.barcode}>
                    <td>{p.barcode}</td>
                    <td>
                      {isEditing ? (
                        <input value={editValues.name} onChange={(e) => setEditValues((prev) => ({ ...prev, name: e.target.value }))} />
                      ) : p.name}
                    </td>
                    <td>
                      {isEditing ? (
                        <input type="number" value={editValues.invoicePrice} onChange={(e) => setEditValues((prev) => ({ ...prev, invoicePrice: e.target.value }))} />
                      ) : formatNumber(p.invoicePrice || 0)}
                    </td>
                    <td>
                      {isEditing ? (
                        <input type="number" value={editValues.billingPrice} onChange={(e) => setEditValues((prev) => ({ ...prev, billingPrice: e.target.value }))} />
                      ) : formatNumber(p.price || 0)}
                    </td>
                    <td>
                      {isEditing ? (
                        <input type="number" value={editValues.stock} onChange={(e) => setEditValues((prev) => ({ ...prev, stock: e.target.value }))} />
                      ) : formatNumber(p.stock || 0)}
                    </td>
                    <td>{formatNumber(Number(p.price || 0) * Number(p.stock || 0))}</td>
                    <td>{p.supplierName || "-"}</td>
                    <td>
                      {isAdmin ? (
                        isEditing ? (
                          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                            <button className="btn" type="button" onClick={() => saveEdit(p.barcode)}>Save</button>
                            <button className="btn secondary" type="button" onClick={cancelEdit}>Cancel</button>
                          </div>
                        ) : (
                          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                            <button className="btn secondary" type="button" onClick={() => startEdit(p)}>Edit</button>
                            <button className="btn secondary" type="button" onClick={() => deleteProduct(p.barcode)}>Delete</button>
                          </div>
                        )
                      ) : (
                        <span style={{ color: "var(--muted)" }}>Read-only</span>
                      )}
                    </td>
                  </tr>
                );
              })}
              {visibleProducts.length === 0 && (
                <tr>
                  <td colSpan="8" style={{ textAlign: "center", padding: 16 }}>No products match your filters</td>
                </tr>
              )}
            </tbody>
          </table>
          </div>

        </div>
      </div>

      {showAddStock && isAdmin && (
        <div className="overlay" onClick={() => setShowAddStock(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
              <h3 style={{ margin: 0 }}>Add Stock</h3>
              <button className="btn ghost" type="button" onClick={() => setShowAddStock(false)}>Close</button>
            </div>
            <form className="form" onSubmit={createProduct}>
              <div className="form-grid-2">
                <div className="input-group"><input aria-label="Barcode" placeholder="Barcode" value={barcode} onChange={(e) => setBarcode(e.target.value)} required /></div>
                <div className="input-group"><input aria-label="Name" placeholder="Product name" value={name} onChange={(e) => setName(e.target.value)} required /></div>
                <div className="input-group"><input type="number" aria-label="Invoice price" placeholder="Invoice price" value={invoicePrice} onChange={(e) => setInvoicePrice(e.target.value)} required /></div>
                <div className="input-group"><input type="number" aria-label="Billing price" placeholder="Billing price" value={billingPrice} onChange={(e) => setBillingPrice(e.target.value)} required /></div>
                <div className="input-group"><input type="number" aria-label="Stock" placeholder="Stock quantity" value={stock} onChange={(e) => setStock(e.target.value)} /></div>
                <div className="input-group"><input type="date" aria-label="Received date" value={receivedDate} onChange={(e) => setReceivedDate(e.target.value)} /></div>
                <div className="input-group form-full"><input aria-label="Supplier name" value={supplierName} onChange={(e) => setSupplierName(e.target.value)} placeholder="Supplier name" /></div>
                <div className="input-group form-full"><input aria-label="Supplier invoice no" value={supplierInvoiceNo} onChange={(e) => setSupplierInvoiceNo(e.target.value)} placeholder="Supplier invoice no" /></div>
              </div>
              <div className="payment-row">
                <span style={{ fontSize: 12, color: "var(--muted)", fontWeight: 600 }}>Supplier Payment Method</span>
                <label><input type="radio" name="spm" checked={supplierPaymentMethod === "cash"} onChange={() => setSupplierPaymentMethod("cash")} /> cash</label>
                <label><input type="radio" name="spm" checked={supplierPaymentMethod === "credit"} onChange={() => setSupplierPaymentMethod("credit")} /> credit</label>
                <label><input type="radio" name="spm" checked={supplierPaymentMethod === "cheque"} onChange={() => setSupplierPaymentMethod("cheque")} /> cheque</label>
              </div>
              <label className="btn ghost" style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                Invoice Photo
                <input
                  type="file"
                  accept="image/*"
                  capture="environment"
                  style={{ display: "none" }}
                  onChange={async (e) => {
                    const file = e.target.files?.[0];
                    if (!file) return;
                    const reader = new FileReader();
                    reader.onload = () => setInvoicePhoto(String(reader.result || ""));
                    reader.readAsDataURL(file);
                  }}
                />
              </label>
              {invoicePhoto ? <img src={invoicePhoto} alt="Invoice preview" style={{ maxWidth: 220, borderRadius: 8 }} /> : null}
              <div className="modal-actions">
                <button className="btn secondary" type="button" onClick={() => setShowAddStock(false)}>Cancel</button>
                <button className="btn" type="submit" disabled={loading}>Save Product</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}










